import { type Instrument, type Market, quantitySteps, type Timeframe, VENUES } from "@trader/shared/MarketData"
import {
  ClientOrderId,
  formatDecimal,
  PlaceOrder,
  PositiveDecimal,
  type Product,
  type Side,
  type AccountSummary,
  type ClockCommand,
  type TimeInForce
} from "@trader/shared/Money"
import { BigDecimal, Option, Schema } from "effect"
import { PRODUCTS_BY_MARKET } from "./format.ts"

// The order ticket's rules, without React (docs/design/0005, OrderTicket): the
// form, its validation through the shared PlaceOrder schema (the same one the
// server decodes), lot and tick steppers, and the client order id's lifecycle.

type Decimal = BigDecimal.BigDecimal

export type TicketType = "Market" | "Limit" | "Stop" | "StopLimit"

export const TICKET_TYPES: ReadonlyArray<readonly [TicketType, string]> = [
  ["Market", "Market"],
  ["Limit", "Limit"],
  ["Stop", "Stop"],
  ["StopLimit", "Stop limit"]
]

/** What the user typed. Numbers stay text until validated, so nothing goes through a float. */
export interface TicketForm {
  readonly side: Side
  readonly type: TicketType
  readonly qty: string
  readonly limitPrice: string
  readonly stopPrice: string
  readonly product: Product
  readonly tif: TimeInForce
  /** Optional (docs/design/0013): put on the position when the order fills; "" for none. */
  readonly stopLoss: string
  readonly takeProfit: string
}

/** The instrument facts the ticket needs, as exact decimals. */
export interface TicketInstrument {
  readonly id: Instrument["id"]
  readonly market: Market
  readonly currency: string
  readonly tickSize: Decimal
  readonly lotSize: Decimal
  /** The quantity's smallest step: 0.01 for a CFD, the lot otherwise (docs/design/0013). */
  readonly qtyStep: Decimal
  /** What − / + add: 0.1 for a CFD, the lot otherwise. */
  readonly qtyNudge: Decimal
  /** A CFD: fractions of a unit, not whole shares. */
  readonly fractional: boolean
  readonly pricePrecision: number
}

const ONE = BigDecimal.fromBigInt(1n)

/** A catalog number (0.05) as an exact decimal through its shortest round-trip text. */
export const catalogDecimal = (n: number, fallback: Decimal = ONE): Decimal =>
  Number.isFinite(n) && n > 0 ? BigDecimal.normalize(BigDecimal.fromNumberUnsafe(n)) : fallback

export const ticketInstrument = (instrument: Instrument): TicketInstrument => ({
  id: instrument.id,
  market: VENUES[instrument.venue].market,
  currency: instrument.currency,
  tickSize: catalogDecimal(instrument.tickSize, BigDecimal.fromStringUnsafe("0.01")),
  lotSize: catalogDecimal(Math.max(1, Math.round(instrument.lotSize))),
  ...(() => {
    const steps = quantitySteps(instrument.venue, instrument.lotSize)
    return { qtyStep: BigDecimal.fromStringUnsafe(steps.step), qtyNudge: BigDecimal.fromStringUnsafe(steps.nudge), fractional: steps.fractional }
  })(),
  pricePrecision: instrument.pricePrecision
})

/** Intraday (MIS) is squared off on intraday bars; 4h and daily accounts can't hold it. */
export const INTRADAY_TIMEFRAMES: ReadonlyArray<Timeframe> = ["1m", "3m", "5m", "15m", "1h"]

export interface ProductChoice {
  readonly product: Product
  readonly enabled: boolean
  /** Why it is disabled. */
  readonly reason: string | null
}

/** The products the ticket offers on a market, for an account of this timeframe. */
export const productChoices = (market: Market, timeframe: Timeframe | null): ReadonlyArray<ProductChoice> =>
  PRODUCTS_BY_MARKET[market].map((product) =>
    product === "Intraday" && timeframe !== null && !INTRADAY_TIMEFRAMES.includes(timeframe)
      ? { product, enabled: false, reason: `Intraday needs an account on 1h bars or shorter; this one uses ${timeframe}.` }
      : { product, enabled: true, reason: null }
  )

/**
 * The product a ticket starts on, and moves to when its side changes (unless the user picked one):
 * Carryforward where Cash can't do the job and Carryforward is offered: a CFD (there's nothing to
 * take delivery of), or a sell when the Cash position holds none to sell (docs/design/0013).
 */
export const suggestedProduct = (
  choices: ReadonlyArray<ProductChoice>,
  market: Market,
  side: Side,
  heldInCash: Decimal
): Product => {
  const carryforward = choices.some((choice) => choice.product === "Carryforward" && choice.enabled)
  const cashCantDoIt = market === "CFD" || (side === "Sell" && !BigDecimal.isPositive(heldInCash))
  return carryforward && cashCantDoIt ? "Carryforward" : "Cash"
}

/**
 * Why an account can't place orders on an instrument (archived, halted, an index); null when it can.
 * Another currency isn't a reason: its P&L is converted (docs/design/0018; the server refuses only
 * when it has no rate).
 */
export const ticketBlockedReason = (account: AccountSummary, instrument: Instrument): string | null => {
  if (account.status === "Archived") return "This account was reset and is read-only. Pick its replacement."
  if (account.status === "Halted") return `The account is halted${account.haltReason ? ` (${account.haltReason})` : ""}; new orders are refused.`
  if (instrument.kind === "index") return `${instrument.id} is an index and can't be traded.`
  return null
}

/** "Prices in EUR · P&L in USD at the previous day's ECB rate" when the currencies differ; else null. */
export const conversionNote = (account: AccountSummary, instrument: Instrument): string | null =>
  instrument.currency === account.currency
    ? null
    : `Prices in ${instrument.currency} · P&L, charges and margin in ${account.currency} at the previous day's ECB rate`

/** One of the chart's right-click orders. */
export interface ChartOrderChoice {
  readonly side: Side
  readonly type: "Limit" | "Stop"
}

/**
 * TradingView's right-click orders at a chart price: above the last price a sell limit and a buy
 * stop, below it a buy limit and a sell stop; none at the last price itself (or without one).
 */
export const chartOrderChoices = (price: Decimal, last: Decimal | null): ReadonlyArray<ChartOrderChoice> =>
  last === null || BigDecimal.equals(price, last)
    ? []
    : BigDecimal.isGreaterThan(price, last)
    ? [{ side: "Sell", type: "Limit" }, { side: "Buy", type: "Stop" }]
    : [{ side: "Buy", type: "Limit" }, { side: "Sell", type: "Stop" }]

/** A quantity typed in the chart's Buy / Sell box, if it's a positive multiple of the step (whole lots, or 0.01 of a CFD); null otherwise. */
export const acceptedQty = (text: string, step: Decimal): string | null => {
  const parsed = parseDecimal(text)
  return Option.isSome(parsed) && BigDecimal.isPositive(parsed.value) && isMultipleOf(parsed.value, step)
    ? formatDecimal(parsed.value)
    : null
}

/**
 * A price as TradingView's Buy / Sell buttons show it: the whole part small, the decimals big
 * ("29,753" "30"); without decimals, the last two digits big.
 */
export const buttonPrice = (price: number, precision: number, locale = "en-IN"): { readonly small: string; readonly big: string } => {
  const digits = Math.max(0, precision)
  const text = new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(price)
  const dot = text.lastIndexOf(".")
  if (dot >= 0) return { small: text.slice(0, dot), big: text.slice(dot + 1) }
  const whole = Math.round(price)
  return { small: new Intl.NumberFormat(locale).format(Math.trunc(whole / 100)), big: String(Math.abs(whole) % 100).padStart(2, "0") }
}

/** The quantity a ticket or a chart order starts with: the last one used on the instrument (if it's still on the step), else `fallback` (one lot). */
export const startingQty = (remembered: string | null, step: Decimal, fallback: Decimal = step): string => {
  const parsed = remembered === null ? Option.none() : parseDecimal(remembered)
  return Option.isSome(parsed) && BigDecimal.isPositive(parsed.value) && isMultipleOf(parsed.value, step) ? formatDecimal(parsed.value) : formatDecimal(fallback)
}

// --- Exact decimal helpers ---------------------------------------------------------------

/** Parses what the user typed ("1,500", " 10 ") as a decimal; none when it isn't one. */
export const parseDecimal = (text: string): Option.Option<Decimal> => {
  const cleaned = text.replace(/[,\s_]/g, "")
  return /^\d+(\.\d+)?$/.test(cleaned) ? BigDecimal.fromString(cleaned) : Option.none()
}

/** `value` rounded to the nearest multiple of `step` (step > 0), half away from zero. */
export const roundToStep = (value: Decimal, step: Decimal): Decimal =>
  BigDecimal.normalize(BigDecimal.multiply(BigDecimal.round(BigDecimal.divideUnsafe(value, step), { scale: 0, mode: "half-from-zero" }), step))

const isMultipleOf = (value: Decimal, step: Decimal): boolean => BigDecimal.isInteger(BigDecimal.divideUnsafe(value, step))

/** Text for a decimal in an input: plain digits, with exactly `decimals` fraction digits (prices) or as is. */
export const decimalInput = (value: Decimal, decimals?: number): string => {
  if (decimals === undefined || decimals < 0) return formatDecimal(value)
  const rounded = BigDecimal.scale(BigDecimal.round(value, { scale: decimals, mode: "half-from-zero" }), decimals)
  const negative = rounded.value < 0n
  const digits = (negative ? -rounded.value : rounded.value).toString().padStart(decimals + 1, "0")
  const text = decimals > 0 ? `${digits.slice(0, -decimals)}.${digits.slice(-decimals)}` : digits
  return negative ? `-${text}` : text
}

/** One lot more or less (never below one lot). */
export const stepQty = (text: string, lot: Decimal, direction: 1 | -1): string => {
  const current = Option.getOrElse(parseDecimal(text), () => BigDecimal.fromBigInt(0n))
  const snapped = roundToStep(current, lot)
  const next = direction === 1 ? BigDecimal.sum(snapped, lot) : BigDecimal.subtract(snapped, lot)
  return formatDecimal(BigDecimal.isGreaterThan(next, lot) ? next : lot)
}

/** One tick up or down, snapped to the grid (never to zero or below). */
export const stepPrice = (text: string, instrument: TicketInstrument, direction: 1 | -1, fallback: Decimal | null): string => {
  const base = Option.getOrNull(parseDecimal(text)) ?? fallback
  if (base === null) return text
  const snapped = roundToStep(base, instrument.tickSize)
  const next = direction === 1 ? BigDecimal.sum(snapped, instrument.tickSize) : BigDecimal.subtract(snapped, instrument.tickSize)
  return decimalInput(BigDecimal.isPositive(next) ? next : instrument.tickSize, instrument.pricePrecision)
}

/** A default price for a new limit or stop field: the last price on the tick grid. */
export const defaultPrice = (last: number | null, instrument: TicketInstrument): string => {
  if (last === null || !Number.isFinite(last) || last <= 0) return ""
  const parsed = BigDecimal.fromNumber(last)
  return Option.isSome(parsed) ? decimalInput(roundToStep(parsed.value, instrument.tickSize), instrument.pricePrecision) : ""
}

// --- Validation ----------------------------------------------------------------------------

export type TicketField = "qty" | "limitPrice" | "stopPrice" | "product" | "stopLoss" | "takeProfit"
export type TicketErrors = Partial<Record<TicketField, string>>

/** What a stop-loss and take-profit risk and aim for, in money and as reward ÷ risk. */
export interface Bracket {
  readonly risk: Decimal | null
  readonly reward: Decimal | null
  readonly ratio: Decimal | null
}

export type TicketCheck =
  | { readonly ok: true; readonly order: PlaceOrder; readonly notional: Decimal | null; readonly bracket: Bracket | null }
  | { readonly ok: false; readonly errors: TicketErrors }

const decodePositive = Schema.decodeUnknownOption(PositiveDecimal)
const decodePlaceOrder = Schema.decodeUnknownResult(PlaceOrder)

const usesLimit = (type: TicketType) => type === "Limit" || type === "StopLimit"
const usesStop = (type: TicketType) => type === "Stop" || type === "StopLimit"

const checkPrice = (text: string, label: string, instrument: TicketInstrument): { value: Decimal | null; error: string | null } => {
  if (text.trim() === "") return { value: null, error: `Enter a ${label} price.` }
  const parsed = parseDecimal(text)
  const positive = Option.flatMap(parsed, (value) => decodePositive(formatDecimal(value)))
  if (Option.isNone(positive)) return { value: null, error: `The ${label} price must be a positive number.` }
  if (!isMultipleOf(positive.value, instrument.tickSize)) {
    return { value: null, error: `Use a multiple of the tick size ${formatDecimal(instrument.tickSize)}.` }
  }
  return { value: positive.value, error: null }
}

/**
 * Checks the form and builds the PlaceOrder the server will receive, decoded
 * through the shared schema. `reference` (the last price) estimates a market
 * order's value; it is display only.
 */
export const checkTicket = (
  form: TicketForm,
  instrument: TicketInstrument,
  clientOrderId: string,
  allowed: ReadonlyArray<ProductChoice>,
  reference: Decimal | null
): TicketCheck => {
  const errors: TicketErrors = {}

  const qty = Option.flatMap(parseDecimal(form.qty), (value) => decodePositive(formatDecimal(value)))
  if (Option.isNone(qty)) errors.qty = "Enter a quantity above zero."
  else if (instrument.fractional) {
    if (!isMultipleOf(qty.value, instrument.qtyStep)) errors.qty = `Use steps of ${formatDecimal(instrument.qtyStep)} (e.g. 1.6).`
  } else if (!BigDecimal.isInteger(qty.value)) errors.qty = "Quantities are whole shares."
  else if (!isMultipleOf(qty.value, instrument.lotSize)) errors.qty = `Use a multiple of the lot size ${formatDecimal(instrument.lotSize)}.`

  const limit = usesLimit(form.type) ? checkPrice(form.limitPrice, "limit", instrument) : { value: null, error: null }
  if (limit.error !== null) errors.limitPrice = limit.error
  const stop = usesStop(form.type) ? checkPrice(form.stopPrice, "stop", instrument) : { value: null, error: null }
  if (stop.error !== null) errors.stopPrice = stop.error
  if (form.type === "StopLimit" && limit.value !== null && stop.value !== null) {
    if (form.side === "Buy" && BigDecimal.isLessThan(limit.value, stop.value)) {
      errors.limitPrice = "A buy stop-limit's limit must be at or above its stop."
    } else if (form.side === "Sell" && BigDecimal.isGreaterThan(limit.value, stop.value)) {
      errors.limitPrice = "A sell stop-limit's limit must be at or below its stop."
    }
  }

  // The price the entry should fill at: what its stop-loss and take-profit are judged against.
  const entry = limit.value ?? stop.value ?? reference
  const long = form.side === "Buy"
  const leg = (text: string, name: "stop-loss" | "take-profit"): { value: Decimal | null; error: string | null } => {
    if (text.trim() === "") return { value: null, error: null }
    const checked = checkPrice(text, name, instrument)
    if (checked.value === null || entry === null) return checked
    const below = name === "stop-loss" ? long : !long
    const wrongSide = below ? !BigDecimal.isLessThan(checked.value, entry) : !BigDecimal.isGreaterThan(checked.value, entry)
    if (wrongSide) {
      const of = limit.value !== null ? "the limit price" : stop.value !== null ? "the stop price" : "the last price"
      return { value: null, error: `A ${long ? "buy" : "sell"}'s ${name} must be ${below ? "below" : "above"} ${of} (${formatDecimal(entry)}).` }
    }
    return checked
  }
  const stopLoss = leg(form.stopLoss, "stop-loss")
  if (stopLoss.error !== null) errors.stopLoss = stopLoss.error.replace("The stop-loss price", "The stop-loss")
  const takeProfit = leg(form.takeProfit, "take-profit")
  if (takeProfit.error !== null) errors.takeProfit = takeProfit.error.replace("The take-profit price", "The take-profit")

  const choice = allowed.find((c) => c.product === form.product)
  if (choice === undefined) errors.product = `${form.product} isn't available on this market.`
  else if (!choice.enabled) errors.product = choice.reason ?? `${form.product} isn't available.`

  if (Object.keys(errors).length > 0 || Option.isNone(qty)) return { ok: false, errors }

  const text = (value: Decimal | null) => (value === null ? "" : formatDecimal(value))
  const type = form.type === "Market"
    ? { _tag: "Market" }
    : form.type === "Limit"
    ? { _tag: "Limit", limitPrice: text(limit.value) }
    : form.type === "Stop"
    ? { _tag: "Stop", stopPrice: text(stop.value) }
    : { _tag: "StopLimit", stopPrice: text(stop.value), limitPrice: text(limit.value) }
  // The wire form, decoded with the schema the server uses: what passes here is what it receives.
  const decoded = decodePlaceOrder({
    clientOrderId,
    instrumentId: instrument.id,
    side: form.side,
    qty: formatDecimal(qty.value),
    type,
    product: form.product,
    tif: form.tif,
    ...(stopLoss.value === null && takeProfit.value === null
      ? {}
      : {
        protection: {
          ...(stopLoss.value === null ? {} : { stopLoss: text(stopLoss.value) }),
          ...(takeProfit.value === null ? {} : { takeProfit: text(takeProfit.value) })
        }
      })
  })
  if (decoded._tag === "Failure") return { ok: false, errors: { qty: "The order isn't valid; check its fields." } }
  const notional = entry === null ? null : BigDecimal.multiply(entry, qty.value)
  return { ok: true, order: decoded.success, notional, bracket: bracketOf(entry, qty.value, stopLoss.value, takeProfit.value) }
}

/** Risk to the stop-loss and reward to the take-profit of `qty` filled at `entry`. */
export const bracketOf = (entry: Decimal | null, qty: Decimal, stopLoss: Decimal | null, takeProfit: Decimal | null): Bracket | null => {
  if (entry === null || (stopLoss === null && takeProfit === null)) return null
  const distance = (price: Decimal | null) => (price === null ? null : BigDecimal.multiply(BigDecimal.abs(BigDecimal.subtract(price, entry)), qty))
  const risk = distance(stopLoss)
  const reward = distance(takeProfit)
  const ratio = risk === null || reward === null || BigDecimal.isZero(risk)
    ? null
    : BigDecimal.round(BigDecimal.divideUnsafe(reward, risk), { scale: 2, mode: "half-from-zero" })
  return { risk, reward, ratio }
}

// --- Client order ids ------------------------------------------------------------------------

const ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyz"

/**
 * A new idempotency key for one order ticket: "t-<time>-<random>". It is minted
 * when the ticket opens and again after any 4xx (needsNewClientOrderId).
 */
export const mintClientOrderId = (
  now: number = Date.now(),
  random: (size: number) => Uint8Array = (size) => crypto.getRandomValues(new Uint8Array(size))
): ClientOrderId => {
  const bytes = random(10)
  let suffix = ""
  for (const byte of bytes) suffix += ALPHABET[byte % ALPHABET.length]
  return ClientOrderId.make(`t-${now.toString(36)}-${suffix}`)
}

/** A key for closing a position ("close-…"), one per click. */
export const mintCloseOrderId = (): ClientOrderId => ClientOrderId.make(`close-${mintClientOrderId().slice(2)}`)

/**
 * Opening the ticket on a playing replay pauses it (docs/design/0011: the user chose time to think);
 * after placing the order they press Play again. Nothing to do otherwise.
 */
export const pauseForTicket = (account: AccountSummary): ClockCommand | null =>
  account.clock._tag === "Replay" && account.clock.status === "Playing" ? { _tag: "Pause" } : null
